import {
    KontentMedia,
    KontentRadioMultiChoice,
    KontentStringValue,
} from '../../../../types/KontentProps';
import {
    requirePageDataset,
    usePageDataset,
    usePageDatasets,
} from '../../../templates/PageDatasetsContext';
// @ts-ignore
import countrywiseSlugs from '../../../../countrywiseSlugs.json';

export interface BlogMetadata {
    id: number;
    elements: {
        title: KontentStringValue;
        short_description: KontentStringValue;
        explore_all_blogs_image: KontentMedia;
        blog_scroll_image: KontentMedia;
    };
}

type NoFollowChoices = 'yes' | 'no';

export interface BlogPagePreview {
    id?: string;
    preferred_language: string;
    elements: {
        blog_topic: KontentRadioMultiChoice<string>;
        wellness_type: KontentRadioMultiChoice<string>;
        slug: {
            value: string;
            url: string;
            web_spotlight: boolean;
        };
        other_metadata: { value: BlogMetadata[] };
        no_follow: KontentRadioMultiChoice<NoFollowChoices>;
        page_components: {
            value: {
                system: {
                    type: string;
                };
                readingTime?: {
                    minutes: number;
                    text: string;
                    time: number;
                    words: number;
                };
                elements?: {
                    display_publish_date?: KontentStringValue;
                    product_0acaef2?: {
                        value: [
                            {
                                elements: {
                                    slug: {
                                        value: string;
                                        url: string;
                                    };
                                };
                            }
                        ];
                    };
                };
            }[];
        };
    };
}

export interface BlogPageQuery {
    allKontentItemLayoutMasterPage: {
        nodes: BlogPagePreview[];
    };
}

/**
 * Blogs picked at build time (gatsby-node.js) for a relative BlogScroll,
 * keyed by the BlogScroll component's system id, in display order.
 */
export const useBlogScrollSelection = (
    componentId?: string,
    required = true
): BlogPagePreview[] => {
    const datasets = usePageDatasets();
    if (!required) return [];
    const selections = requirePageDataset(datasets, 'blogScrollSelections');
    if (
        !componentId ||
        !Object.prototype.hasOwnProperty.call(selections, componentId)
    ) {
        throw new Error(
            `Missing BlogScroll selection for component "${
                componentId ?? 'unknown'
            }". ` + 'Check getPageDatasetContext and the component system id.'
        );
    }
    const ids: string[] = selections[componentId];
    const nodes: BlogPagePreview[] = requirePageDataset(
        datasets,
        'pageBlogScrollBlogs'
    ).nodes;
    const byId = new Map(nodes.map((node) => [node.id, node]));

    return ids.map((id) => {
        const node = byId.get(id);
        if (!node) {
            throw new Error(
                `Missing selected blog "${id}" for BlogScroll component "${componentId}".`
            );
        }
        return node;
    });
};

export const useBlogs = (
    prefLanguage?: string,
    required = true
): BlogPagePreview[] => {
    const blogPagePreviews: BlogPageQuery = {
        allKontentItemLayoutMasterPage: {
            nodes: usePageDataset('pageBlogs', required)?.nodes ?? [],
        },
    };

    const validSlugs = new Set<string>(
        (countrywiseSlugs as Record<string, string[]>)[
            prefLanguage?.toLowerCase() || 'default'
        ] || []
    );

    const sameLanguageBlogs =
        blogPagePreviews.allKontentItemLayoutMasterPage.nodes.filter(
            (blog) =>
                blog.preferred_language?.toLowerCase() ===
                    prefLanguage?.toLowerCase() &&
                validSlugs.has(blog.elements?.slug?.value)
        );

    const sortedBlogs = sameLanguageBlogs.sort((leftNode, rightNode) => {
        const getPublishDate = (node: BlogPagePreview) => {
            const mastheadComponent =
                node.elements?.page_components?.value?.filter(
                    (component) =>
                        component?.system?.type === 'component___blog_masthead'
                )[0];

            return mastheadComponent?.elements?.display_publish_date?.value;
        };

        let leftPublishDate = getPublishDate(leftNode);

        let rightPublishDate = getPublishDate(rightNode);

        if (!leftPublishDate) {
            return 1;
        }

        if (rightPublishDate && rightPublishDate > leftPublishDate) {
            return 1;
        }

        return -1;
    });

    return sortedBlogs;
};
